import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Stack } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import { useStyles } from '../styles/sectionStyles';
import SectionHeader from './SectionHeader';
import TaskCard from './TaskCard';
import AddTaskForm from '../../projects/boardview/components/AddTaskForm';
import { getSectionsByProjectId } from '../../../services/sectionService';
import { getProjectById, getStakeholderVisibility } from '../../../services/projectService';
import { updateTaskOrder } from '../../../services/taskService';
import { isAdmin, isClient, isStakeholder } from '../../../utils/share';
import { Loader } from '../../../components/loader';
import { PROJECT_EDITOR_ROLE } from '../../../Constants/ProjectConstants';

const Section = ({ section, setLoading, runningTask, setRunningTask, handleTaskClick, allTask, onRefetch }) => {
  const classes = useStyles({ isOver: false });
  const [taskList, setTaskList] = useState(allTask);
  const [taskCount, setTaskCount] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [firstSectionId, setFirstSectionId] = useState(1); // Default fallback
  const [issuesSectionId, setIssuesSectionId] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [stakeholderProjectMembersVisible, setStakeholderProjectMembersVisible] = useState(true);
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const accountData = useSelector((state) => state?.login?.user);

  const userIsAdmin = isAdmin();
  const userIsStakeholder = isStakeholder() || isClient();

  // Fetch sections for the project to get the first section ID
  useEffect(() => {
    const fetchSections = async () => {
      if (section?.id) {
        try {
          const response = await getSectionsByProjectId(section.id);
          const sections = response?.data?.data?.rows || response?.data?.data || [];
          if (sections.length > 0) {
            setFirstSectionId(sections[0].id);
            const issues = sections.find(
              (s) => (s?.name || '').toLowerCase().includes('issues') || (s?.name || '').toLowerCase().includes('issue')
            );
            if (issues?.id) setIssuesSectionId(issues.id);
          }
        } catch (error) {
          console.error('Error fetching sections:', error);
        }
      }
    };
    fetchSections();
  }, [section?.id]);

  // Fetch project details if user is admin or stakeholder (to get project members for assignee dropdown)
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (section?.id) {
        try {
          const response = await getProjectById(section.id);
          setProjectDetails(response?.data?.data);
        } catch (error) {
          console.error('Error fetching project details:', error);
        }
      }
    };

    fetchProjectDetails();
  }, [userIsAdmin, userIsStakeholder, section?.id]);

  useEffect(() => {
    const fetchStakeholderProjectMembersVisibility = async () => {
      if (!section?.id || !userIsStakeholder) {
        setStakeholderProjectMembersVisible(true);
        return;
      }

      try {
        const response = await getStakeholderVisibility(section.id);
        const canSeeProjectMembers = response?.data?.data?.stakeholderVisibility?.overview?.projectMembers !== false;
        setStakeholderProjectMembersVisible(canSeeProjectMembers);
      } catch (error) {
        console.error('Error fetching stakeholder visibility:', error);
        setStakeholderProjectMembersVisible(true);
      }
    };

    fetchStakeholderProjectMembersVisibility();
  }, [section?.id, userIsStakeholder]);

  useEffect(() => {
    const projectTasks = (allTask?.filter((item) => item?.projectId === section?.id) || []).sort((a, b) => {
      // Sort by sequence, then by id if sequence is the same
      const seqA = a.sequence ?? 0;
      const seqB = b.sequence ?? 0;
      if (seqA !== seqB) return seqA - seqB;
      return (a.id || 0) - (b.id || 0);
    });
    setTaskCount(projectTasks.length);
    setTaskList(projectTasks); // Update taskList when allTask changes
    // Turn off section loading when data is updated
    if (isSectionLoading) {
      setIsSectionLoading(false);
    }
  }, [allTask, section?.id, isSectionLoading]);

  const handleToggleAddForm = useCallback(() => {
    setShowAddForm((prev) => !prev);
  }, [section?.id]);

  const handleAddFormCancel = () => {
    setShowAddForm(false);
  };

  const handleAddFormSubmit = () => {
    setShowAddForm(false);
    setIsSectionLoading(true);
    // Trigger a refetch to update the task list after task creation (without page loader)
    if (onRefetch) {
      onRefetch(false);
    }
  };

  const roleName = projectDetails?.projectmember?.find((item) => item?.userId === accountData?.id)?.projectRoles
    ?.roleName;
  const isEditPermission = PROJECT_EDITOR_ROLE?.includes(roleName);
  const canShowAssigneeDropdown =
    (userIsAdmin || isEditPermission || roleName !== 'Team Members') &&
    (!userIsStakeholder || stakeholderProjectMembersVisible);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    // Optimistically update the UI
    const newTaskList = Array.from(taskList);
    const [reorderedTask] = newTaskList.splice(sourceIndex, 1);
    newTaskList.splice(destinationIndex, 0, reorderedTask);
    setTaskList(newTaskList);

    // Update sequence values for all tasks in the project
    const taskOrders = newTaskList.map((task, index) => ({
      taskId: task.id,
      sequence: index + 1,
    }));

    try {
      await updateTaskOrder(taskOrders);
      // Refetch to ensure data is in sync
      if (onRefetch) {
        onRefetch(false);
      }
    } catch (error) {
      // Revert on error
      const projectTasks = (allTask?.filter((item) => item?.projectId === section?.id) || []).sort((a, b) => {
        const seqA = a.sequence ?? 0;
        const seqB = b.sequence ?? 0;
        if (seqA !== seqB) return seqA - seqB;
        return (a.id || 0) - (b.id || 0);
      });
      setTaskList(projectTasks);
      toast.error('Failed to update task order. Please try again.');
    }
  };

  return (
    <Box ref={sectionRef} className={classes.section}>
      <SectionHeader onAddClick={handleToggleAddForm} section={section} taskCount={taskCount} />
      {isSectionLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100px',
            p: 2,
          }}
        >
          <Loader size={40} />
        </Box>
      ) : (
        <Box ref={scrollContainerRef} className={classes?.taskStack} id={`scrollableDiv-${section?.id}`}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`project-${section?.id}`}>
              {(provided) => (
                <Stack spacing={1} {...provided.droppableProps} ref={provided.innerRef}>
                  {taskList?.map((task, index) => {
                    if (task?.projectId !== section?.id) return null;

                    if (task?.taskName?.trim()?.toLowerCase() === 'not aware') return null;
                    return (
                      <Draggable key={task?.id} draggableId={`task-${task?.id}`} index={index}>
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              opacity: snapshot.isDragging ? 0.8 : 1,
                            }}
                          >
                            <TaskCard
                              task={task}
                              project={section}
                              onClick={handleTaskClick(task, section)} // section is project here
                              setLoading={setLoading}
                              runningTask={runningTask}
                              setTaskList={setTaskList}
                              setRunningTask={setRunningTask}
                              style={{ margin: '0px auto 10px auto' }}
                            />
                          </Box>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {showAddForm && (
                    <AddTaskForm
                      sectionId={userIsStakeholder && issuesSectionId ? issuesSectionId : firstSectionId} // Stakeholder -> Issues
                      projectId={section?.id}
                      onCancel={handleAddFormCancel}
                      onSubmit={handleAddFormSubmit}
                      defaultAssignToSelf
                      projectDetails={
                        userIsAdmin || userIsStakeholder || isEditPermission || roleName !== 'Team Members'
                          ? projectDetails
                          : null
                      }
                      showAssigneeDropdown={canShowAssigneeDropdown}
                    />
                  )}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      )}
    </Box>
  );
};

Section.propTypes = {
  section: PropTypes.object,
  setLoading: PropTypes.func,
  runningTask: PropTypes.object,
  setRunningTask: PropTypes.func,
  handleTaskClick: PropTypes.func,
  allTask: PropTypes.array,
  onRefetch: PropTypes.func,
  // ProjectAddTask Props
  // selected: PropTypes.object,
  // openModal: PropTypes.bool,
  // selectedProject: PropTypes.object,
  // handleClose: PropTypes.func,
  // action: PropTypes.string,
};

export default Section;
